import { useCallback, useEffect, useState } from 'react';
import { logoDark, logoLight } from '@react-apps/design-system';
import { useAuthStore } from '@/modules/auth/presentation/store/auth.store';
import { useProfile } from '@/modules/profile/presentation/hooks/use-profile';

interface HeaderProps {
  isCollapsed: boolean;
  onToggleSidebar: () => void;
}

export function Header({ isCollapsed, onToggleSidebar }: HeaderProps) {
  const { auth } = useAuthStore();
  const { data: profile } = useProfile();

  const displayName = profile?.name ?? auth?.email.split('@')[0] ?? 'Usuário';
  const initials = profile?.name
    ? profile.name
        .split(' ')
        .slice(0, 2)
        .map((w: string) => w[0])
        .join('')
        .toUpperCase()
    : (auth?.email?.[0]?.toUpperCase() ?? 'U');

  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => !prev);
  }, []);

  return (
    <header className="border-(--color-border) bg-(--color-bg-primary) flex h-16 items-center justify-between border-b px-4">
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          className="text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) cursor-pointer rounded-lg p-2 transition-colors"
          aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"
            />
          </svg>
        </button>

        <img
          src={isDark ? logoDark : logoLight}
          alt="Primum"
          className="h-4 w-auto"
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={toggleTheme}
          className="text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) cursor-pointer rounded-lg p-2 transition-colors"
          aria-label="Toggle theme"
        >
          {isDark ? (
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
              />
            </svg>
          ) : (
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
              />
            </svg>
          )}
        </button>

        <div className="hidden flex-col items-end sm:flex">
          <p className="text-(--color-text-primary) text-sm font-semibold leading-tight">
            {displayName}
          </p>
          {auth?.email && (
            <p className="text-(--color-text-secondary) text-xs leading-tight">
              {auth.email}
            </p>
          )}
        </div>

        <div className="bg-(--color-accent) flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium text-white">
          {initials}
        </div>
      </div>
    </header>
  );
}
